import { ReactNode, useRef, useState } from 'react';
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react';
import { createPortal } from 'react-dom';
import { Container, Content, Wrapper } from './index.styles';
import { Positions } from '../../../theme';
import { useClickOutsideChecker } from '../../utils/useClickOutsideChecker';

export interface TooltipProps {
  /**
   * Tooltip information content. HTML content type. Required.
   */
  content?: ReactNode;
  /**
   * Tooltip opening position. Optional.
   */
  defaultPosition?: Positions;
  /**
   * Tooltip delay in milliseconds. Optional.
   */
  delay?: number;
  /**
   * Use max width? Optional.
   */
  maxWidth?: boolean;
  /**
   * Do not underline the text. Optional.
   */
  noDecoration?: boolean;
  /**
   * Trigger on click instead of hover. Optional.
   */
  useClick?: boolean;
  children?: ReactNode;
  className?: string;
  customClass?: string;
  testId?: string;
}

/**
 * Tooltip
 */
export const Tooltip = (props: TooltipProps) => {
  const {
    children,
    className = '',
    content,
    delay,
    noDecoration = false,
    customClass = '',
    defaultPosition = 'top',
    maxWidth,
    testId = 'tooltip',
    useClick = false,
  } = props;

  const [isVisible, setIsVisible] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null!);
  const delayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useClickOutsideChecker(wrapperRef, () => setIsVisible(false), isVisible);

  const { x, y, strategy, refs } = useFloating({
    open: isVisible,
    onOpenChange: setIsVisible,
    placement: defaultPosition,
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  if (!content) return <>{children}</>;

  return (
    <Wrapper
      className={`ds-tooltip-wrapper ${customClass} ${className}`}
      ref={wrapperRef}
    >
      <Container
        aria-describedby="tooltip"
        className={`ds-tooltip ${customClass} ${className}`}
        data-testid={testId}
        ref={(node) => refs.setReference(node)}
        onClick={() => useClick && setIsVisible((prev) => !prev)}
        onPointerEnter={() => {
          if (useClick) return;

          if (delay) {
            delayTimeoutRef.current = setTimeout(() => setIsVisible(true), delay);
          } else {
            setIsVisible(true);
          }
        }}
        onPointerLeave={() => {
          if (useClick) return;

          if (delayTimeoutRef.current) {
            clearTimeout(delayTimeoutRef.current);
            delayTimeoutRef.current = null;
          }

          setIsVisible(false);
        }}
        noDecoration={noDecoration}
      >
        {children}
      </Container>
      {isVisible &&
        createPortal(
          <Content
            data-testid={`${testId}-tip`}
            maxWidth={maxWidth}
            ref={(node) => refs.setFloating(node)}
            role="tooltip"
            style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
          >
            {content}
          </Content>,
          document.getElementById('popover-root') as HTMLElement
        )}
    </Wrapper>
  );
};
